import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, FileText, AlertCircle, CheckCircle2, ArrowRight, Music } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { FestivalCreationHeader } from '../components/FestivalCreationHeader';
import { DocumentDropzone } from '../components/DocumentDropzone';

export default function CreateFestivalDocsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);

  const [insuranceUrl, setInsuranceUrl] = useState<string | null>(null);
  const [municipalUrl, setMunicipalUrl] = useState<string | null>(null);
  const [sacemUrl, setSacemUrl] = useState<string | null>(null);
  const [hasMusic, setHasMusic] = useState(false);
  const [festivalName, setFestivalName] = useState('');

  useEffect(() => {
    async function load() {
      if (!user) return;
      const activeId = sessionStorage.getItem('activeFestivalId') || user.uid;
      try {
        const snap = await getDoc(doc(db, 'festivals', activeId));
        if (snap.exists()) {
          const d = snap.data();
          setInsuranceUrl(d.legalDocs?.insuranceUrl ?? null);
          setMunicipalUrl(d.legalDocs?.municipalAuthUrl ?? null);
          setSacemUrl(d.legalDocs?.sacemUrl ?? null);
          setHasMusic(d.hasMusic ?? d.category === 'music' ?? false);
          setFestivalName(d.name ?? '');
        }
      } catch (err) {
        console.error(err);
      }
    }
    load();
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (!insuranceUrl) {
      alert("L'attestation d'assurance événementielle est obligatoire.");
      return;
    }
    setIsSaving(true);
    const activeId = sessionStorage.getItem('activeFestivalId') || user.uid;
    try {
      await setDoc(doc(db, 'festivals', activeId), {
        legalDocs: {
          insuranceUrl,
          municipalAuthUrl: municipalUrl ?? null,
          sacemUrl: sacemUrl ?? null,
          submittedAt: new Date().toISOString(),
        },
        status: 'pending_review',
        pendingReviewAt: new Date().toISOString(),
      }, { merge: true });

      // Notify matching stands (non-blocking — will fire once admin publishes)
      sessionStorage.removeItem('activeFestivalId');
      navigate('/my-festivals', { state: { pendingReview: true } });
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la soumission. Réessayez.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0F] text-white">
      <FestivalCreationHeader onBack={() => navigate(-1)} />

      <div className="max-w-2xl mx-auto px-6 py-10">
        {/* Step indicator */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-[#FF5C00]">Documents légaux • Étape 7/7</p>
            <div className="flex gap-1.5">
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className={`h-1 w-6 rounded-full transition-all ${i < 7 ? 'bg-[#FF5C00]' : 'bg-white/10'}`} />
              ))}
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#FF5C00]/10 border border-[#FF5C00]/20 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-6 h-6 text-[#FF5C00]" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight">Documents légaux</h1>
              <p className="text-gray-400 mt-1 text-sm leading-relaxed">
                Ces documents sont requis pour la validation de votre festival. Votre événement sera visible aux exposants après vérification par l'équipe Festin (sous 48h).
              </p>
            </div>
          </div>
        </div>

        {/* Info banner */}
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl px-5 py-4 mb-8 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-amber-300">Pourquoi ces documents ?</p>
            <p className="text-xs text-amber-400/80 mt-1 leading-relaxed">
              Un festival sans assurance événementielle engage votre responsabilité civile en cas d'incident. Festin les vérifie pour protéger les deux parties.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Assurance obligatoire */}
          <div className="bg-[#161618] border border-white/8 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-black text-white">Assurance événementielle</span>
              <span className="text-[10px] font-black bg-[#FF5C00]/15 text-[#FF5C00] px-2 py-0.5 rounded-full uppercase tracking-wider">Obligatoire</span>
            </div>
            <p className="text-xs text-gray-500 mb-4">Attestation d'assurance responsabilité civile couvrant votre festival (PDF ou image).</p>
            <DocumentDropzone
              label="Attestation d'assurance RC événementielle"
              storagePath={`festivals/${sessionStorage.getItem('activeFestivalId') || user?.uid}/legal_insurance`}
              onUploadComplete={(url) => setInsuranceUrl(url)}
              existingUrl={insuranceUrl}
              accept="application/pdf,image/*"
            />
            {insuranceUrl && (
              <div className="flex items-center gap-2 mt-3 text-emerald-400 text-xs font-medium">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Document chargé
              </div>
            )}
          </div>

          {/* Autorisation municipale */}
          <div className="bg-[#161618] border border-white/8 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-black text-white">Autorisation municipale</span>
              <span className="text-[10px] font-black bg-white/5 text-gray-400 px-2 py-0.5 rounded-full uppercase tracking-wider">Recommandé</span>
            </div>
            <p className="text-xs text-gray-500 mb-4">Arrêté municipal ou autorisation de la mairie pour l'occupation de l'espace. Peut être fourni ultérieurement si en cours d'obtention.</p>
            <DocumentDropzone
              label="Autorisation municipale"
              storagePath={`festivals/${sessionStorage.getItem('activeFestivalId') || user?.uid}/legal_municipal`}
              onUploadComplete={(url) => setMunicipalUrl(url)}
              existingUrl={municipalUrl}
              accept="application/pdf,image/*"
            />
            {municipalUrl && (
              <div className="flex items-center gap-2 mt-3 text-emerald-400 text-xs font-medium">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Document chargé
              </div>
            )}
          </div>

          {/* SACEM — conditionnel */}
          <div className="bg-[#161618] border border-white/8 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-3">
              <Music className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-black text-white">Déclaration SACEM</span>
              <span className="text-[10px] font-black bg-white/5 text-gray-400 px-2 py-0.5 rounded-full uppercase tracking-wider">Si musique diffusée</span>
            </div>
            <label className="flex items-center gap-3 cursor-pointer mb-4 group">
              <div
                onClick={() => setHasMusic(v => !v)}
                className={`w-10 h-5 rounded-full transition-all relative shrink-0 ${hasMusic ? 'bg-purple-500' : 'bg-white/10'}`}
              >
                <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${hasMusic ? 'translate-x-5' : ''}`} />
              </div>
              <span className="text-sm text-gray-400 group-hover:text-gray-200 transition-colors">Mon festival diffuse de la musique (live ou enregistrée)</span>
            </label>
            {hasMusic && (
              <>
                <p className="text-xs text-gray-500 mb-4">Contrat SACEM ou preuve de déclaration obligatoire pour tout festival diffusant de la musique.</p>
                <DocumentDropzone
                  label="Déclaration SACEM"
                  storagePath={`festivals/${sessionStorage.getItem('activeFestivalId') || user?.uid}/legal_sacem`}
                  onUploadComplete={(url) => setSacemUrl(url)}
                  existingUrl={sacemUrl}
                  accept="application/pdf,image/*"
                />
                {sacemUrl && (
                  <div className="flex items-center gap-2 mt-3 text-emerald-400 text-xs font-medium">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Document chargé
                  </div>
                )}
              </>
            )}
          </div>

          {/* What happens next */}
          <div className="bg-[#0F1623] border border-blue-500/20 rounded-2xl px-5 py-4">
            <p className="text-sm font-bold text-blue-300 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Ce qui se passe après soumission
            </p>
            <ol className="space-y-2">
              {[
                "Votre dossier est transmis à l'équipe Festin pour vérification.",
                "Délai de validation : sous 48h ouvrées.",
                "Dès validation, votre festival est publié et visible aux exposants.",
                "Vous recevez un email de confirmation.",
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-2.5 text-xs text-gray-400">
                  <span className="shrink-0 w-4 h-4 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-[10px] font-black mt-0.5">{i + 1}</span>
                  {step}
                </li>
              ))}
            </ol>
          </div>

          {/* Actions */}
          <div className="flex flex-col md:flex-row gap-3 pt-2 pb-10">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-full md:w-auto px-8 py-4 rounded-2xl border border-white/10 text-white font-bold hover:bg-white/10 transition-all text-sm"
            >
              Précédent
            </button>
            <button
              type="submit"
              disabled={isSaving || !insuranceUrl}
              className="flex-1 flex items-center justify-center gap-3 bg-[#FF5C00] hover:bg-[#e05000] disabled:opacity-40 disabled:cursor-not-allowed text-white py-4 px-6 rounded-2xl font-black text-sm transition-all"
            >
              {isSaving ? (
                <>Soumission en cours…</>
              ) : (
                <>
                  Soumettre pour validation
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
